import {
  Button,
  Col,
  Form,
  Input,
  message,
  Row,
  Select,
  Space,
  Tag,
} from 'antd'
import { uniqBy } from 'lodash'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { Copyable, Icon } from '../../../../components'

import { getReverseDCList } from '../../api'

const Logistics = ({ barcodeErrors, form, selectedStore }) => {
  const barcodeInputRef = useRef(null)

  const [reverseDCs, setReverseDCs] = useState([])
  const [barcodeList, setBarcodeList] = useState(selectedStore?.barcodes || [])

  const handleRemoveNewBarcode = (barcode) => {
    setBarcodeList(barcodeList.filter((b) => b !== barcode))
  }

  useEffect(() => {
    getReverseDCList()
      .then(({ data }) => setReverseDCs(data.data))
      .catch((error) =>
        console.warn(error, 'Fetching reverse dc list failed!'),
      )
  }, [])

  useEffect(() => {
    form.setFieldValue('barcodes_new', barcodeList)
  }, [barcodeList])

  const isBarcodeValid = (barcode) => /^[A-Za-z][0-9]{3}$/.test(barcode)

  const handleAddBarcode = () => {
    const barcodeInput = barcodeInputRef.current.input.value
    const inputBarcodes = uniqBy(
      barcodeInput
        .trim()
        .split(',')
        .map((barcode) => barcode.toUpperCase()),
    )

    const duplicateLocal = inputBarcodes.find((bc) => barcodeList.includes(bc))

    if (duplicateLocal) {
      message.error(`Barcode "${duplicateLocal}" already added!`)
    }

    const invalidBarcode = inputBarcodes.find((bc) => !isBarcodeValid(bc))

    if (invalidBarcode) {
      message.error(
        `Barcode "${invalidBarcode}" is invalid! It must be a letter followed by 3 digits (e.g. A123).`,
      )

      return
    }

    const barcodesToAdd = inputBarcodes.filter(
      (barcode) => !barcodeList.includes(barcode) && isBarcodeValid(barcode),
    )

    if (!barcodesToAdd.length) return

    setBarcodeList([...barcodeList, ...barcodesToAdd])

    barcodeInputRef.current.input.value = ''
  }

  const getColor = (barcode) => {
    if (selectedStore?.barcodes.includes(barcode)) return 'green'
    if (barcodeErrors.includes(barcode)) return 'red'

    return 'blue'
  }

  const returnMethodOptions = [
    { label: 'Tote', value: 'tote' },
    { label: 'Low tech bin', value: 'low_tech_bin' },
    { label: 'RVM', value: 'rvm' },
  ]

  const collectionTypeOptions = [
    { label: 'Direct to stores', value: 'direct_to_stores' },
    { label: 'Reverse DC', value: 'reverse_dc' },
  ]

  const storeFormatOptions = [
    { label: 'Hyper', value: 'hyper' },
    { label: 'Market', value: 'market' },
    { label: 'Proxi', value: 'proxi' },
  ]

  const reverseDCOptions = reverseDCs.map(({ id, name }) => ({ label: name, value: id }))
  const copyAbleStoreId = selectedStore?.tds_store_id

  return (
    <Row style={{ margin: 15 }}>
      <Col span={11}>
        <Form.Item
          label="Collection Type"
          name="collection_type"
          rules={[
            { message: 'Please select a collection type!', required: true },
          ]}
        >
          <Select data-testid="collectionTypeSelect" options={collectionTypeOptions} />
        </Form.Item>
      </Col>

      <Col offset={2} span={11}>
        <Form.Item
          label="Return Method"
          name="return_method"
          rules={[{ message: 'Please select a return method!', required: true }]}
        >
          <Select data-testid="returnMethodSelect" mode="multiple" options={returnMethodOptions} />
        </Form.Item>
      </Col>

      <Col span={11}>
        <Form.Item
          label="Reverse DC"
          name="reverse_dc_id"
          rules={[{ message: 'Please select a Reverse DC!', required: true }]}
        >
          <Select data-testid="reverseDCSelect" options={reverseDCOptions} />
        </Form.Item>
      </Col>

      <Col offset={2} span={11}>
        <Form.Item label="Store Format" name="store_format">
          <Select data-testid="StoreFormatSelect" options={storeFormatOptions} />
        </Form.Item>
      </Col>

      <Col span={11}>
        <Form.Item
          label="Add New Store Barcode"
          name="barcodes"
          style={{ margingBottom: 10 }}
          tooltip={{ icon: <Icon type="infoCircle" />, title: 'Tracking ID for physical container seals. Must follow the A001 format.' }}
        >
          <Space.Compact style={{ width: '100%' }}>
            <Input
              onPressEnter={handleAddBarcode}
              placeholder="Enter barcode"
              ref={barcodeInputRef}
            />
            <Button onClick={handleAddBarcode} type="primary">
              Add
            </Button>
          </Space.Compact>
        </Form.Item>

        <div className="barcode-list" style={{ marginBottom: 20 }}>
          <span>Barcode(s): </span>
          {barcodeList.map((barcode) => (
            <Tag closable={!selectedStore?.barcodes.includes(barcode)} color={getColor(barcode)} key={barcode} onClose={() => handleRemoveNewBarcode(barcode)}>
              {barcode}
            </Tag>
          ))}

          {barcodeErrors.length > 0 && (
            <div style={{ color: '#ff4d4f', marginTop: 4 }}>
              Please remove invalid barcode(s) before save: {barcodeErrors.join(', ')}
            </div>
          )}
        </div>
      </Col>

      <Col offset={2} span={11}>
        <Form.Item label="External ID" name="external_id">
          <Input />
        </Form.Item>

        {copyAbleStoreId &&
          <Form.Item label="TDS Store ID">
            <Copyable alwaysShowIcon text={copyAbleStoreId} />
          </Form.Item>
        }
      </Col>

    </Row>
  )
}

Logistics.propTypes = {
  barcodeErrors: PropTypes.arrayOf(PropTypes.string),
  form: PropTypes.object.isRequired,
  selectedStore: PropTypes.object,
}

export default Logistics
